/**
 * Cel-shaded outline shell — the classic cartoon trick.
 *
 * A slightly enlarged black sphere rendered with BackSide culling sits
 * around the planet body. Front faces are clipped, leaving a thin dark
 * silhouette that hugs the planet from the camera's perspective. Combined
 * with MeshToonMaterial's hard step gradient, this makes objects read as
 * hand-drawn rather than physically rendered.
 */

import {
  BackSide,
  Color,
  Mesh,
  MeshBasicMaterial,
  SphereGeometry,
  type ColorRepresentation,
} from "three";

export interface ToonOutlineOptions {
  /** Inner planet radius. Outline shell is scaled up from this. */
  planetRadius: number;
  /** Outline shell scale relative to planet radius. */
  scale?: number;
  /** Outline color. Slightly tinted near-black reads better than pure black. */
  color?: ColorRepresentation;
  /** Shell mesh segments. Lower keeps it cheap — silhouette is what matters. */
  segments?: number;
}

const DEFAULT_SCALE = 1.06;
const DEFAULT_COLOR = 0x0a0916;
const DEFAULT_SEGMENTS = 32;

export function createToonOutline(opts: ToonOutlineOptions): Mesh {
  const scale = opts.scale ?? DEFAULT_SCALE;
  const color = opts.color ?? DEFAULT_COLOR;
  const seg = opts.segments ?? DEFAULT_SEGMENTS;
  const geometry = new SphereGeometry(opts.planetRadius * scale, seg, Math.round(seg * 0.75));
  const material = new MeshBasicMaterial({
    color: new Color(color),
    side: BackSide,
    fog: false,
  });
  const mesh = new Mesh(geometry, material);
  mesh.name = "toon-outline";
  // Render before the planet so the silhouette is clean even if the
  // planet's depth ordering jitters.
  mesh.renderOrder = -1;
  return mesh;
}

export const __testing = { DEFAULT_SCALE, DEFAULT_COLOR };
